/**
 * SECURITY: Phone number utilities with masking and validation
 * 🔒 Masks phone numbers to prevent exposure
 * 🔒 Provides robust validation for multiple regions
 */

/**
 * UK Phone Number Validation Regex
 * Supports multiple formats:
 * - +44 7xxx xxx xxx
 * - 07xxx xxx xxx
 * - (07xxx) xxx xxx
 */
const UK_PHONE_REGEX = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;

/**
 * International phone validation
 * Supports E.164 format: +[country code][number]
 */
const INTERNATIONAL_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * SECURITY: Masks phone number for safe display
 * Hides most digits, shows only last 4
 * 🔒 Prevents information disclosure on shared devices
 *
 * Examples:
 * +44 7911 123456 → +44 **** **** 56
 * 07911 123456    → 07** *** **56
 * +1 555 123 4567 → +1 *** *** *567
 *
 * @param {string} phoneNumber - Full phone number
 * @returns {string} Masked phone number
 */
export function maskPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "****";

  const normalized = phoneNumber.replace(/\s/g, "");

  // Show country code if present
  if (normalized.startsWith("+")) {
    const countryCode = normalized.substring(0, 3); // +44, +1, etc.
    const lastFour = normalized.slice(-4);
    const hidden = "*".repeat(normalized.length - 7); // Hide middle part

    return `${countryCode} ${hidden} ${lastFour}`;
  }

  // Domestic format
  const lastFour = normalized.slice(-4);
  const hidden = "*".repeat(normalized.length - 6);

  return `${hidden} ${lastFour}`;
}

/**
 * SECURITY: Validates UK phone numbers
 * Supports multiple input formats
 *
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid UK phone
 */
export function isValidUKPhone(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") return false;

  const normalized = phoneNumber.replace(/\s/g, "").replace(/[()]/g, "");

  // Check minimum length
  if (normalized.length < 10) return false;

  // Check maximum length (UK numbers shouldn't exceed 13 chars)
  if (normalized.length > 13) return false;

  return UK_PHONE_REGEX.test(normalized);
}

/**
 * SECURITY: Validates international phone numbers
 * Uses E.164 format
 *
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid international phone
 */
export function isValidInternationalPhone(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") return false;

  const normalized = phoneNumber.replace(/\s/g, "");

  // Must start with +
  if (!normalized.startsWith("+")) return false;

  // Check length (E.164 format: +[country code][number])
  if (normalized.length < 10 || normalized.length > 15) return false;

  return INTERNATIONAL_PHONE_REGEX.test(normalized);
}

/**
 * SECURITY: Formats UK phone number to standard format
 * Converts to international format: +44 xxxx xxxxxx
 *
 * @param {string} number - Phone number to format
 * @returns {string} Formatted phone number
 */
export function formatUKPhoneNumber(number) {
  if (!number) return "";

  const normalized = number.replace(/\s/g, "").replace(/[()]/g, "");

  // Check if it's a UK number starting with 07
  if (normalized.startsWith("07")) {
    return `+44${normalized.slice(1)}`;
  }

  // Check if it's already in international format
  if (normalized.startsWith("+44")) {
    return normalized;
  }

  // If starts with 0 and length suggests UK
  if (normalized.startsWith("0")) {
    return `+44${normalized.slice(1)}`;
  }

  return normalized;
}

/**
 * SECURITY: Validates phone number format before Firebase call
 * Provides user-friendly error messages
 *
 * @param {string} phoneNumber - Phone number to validate
 * @param {string} region - Region code ('UK', 'US', 'INTL')
 * @returns {object} { isValid, error?, maskedNumber }
 */
export function validatePhoneNumber(phoneNumber, region = "UK") {
  if (!phoneNumber) {
    return {
      isValid: false,
      error: "Phone number is required",
      maskedNumber: null,
    };
  }

  const trimmed = phoneNumber.trim();

  // Regional validation
  if (region === "UK" || region === "UK_ONLY") {
    if (!isValidUKPhone(trimmed)) {
      return {
        isValid: false,
        error:
          "Please enter a valid UK phone number (e.g., +44 7911 123456 or 07911 123456)",
        maskedNumber: null,
      };
    }

    const formatted = formatUKPhoneNumber(trimmed);
    return {
      isValid: true,
      error: null,
      maskedNumber: maskPhoneNumber(formatted),
    };
  }

  if (region === "INTL") {
    if (!isValidInternationalPhone(trimmed)) {
      return {
        isValid: false,
        error: "Please enter a valid international phone number (e.g., +1 555 123 4567)",
        maskedNumber: null,
      };
    }

    return {
      isValid: true,
      error: null,
      maskedNumber: maskPhoneNumber(trimmed),
    };
  }

  return {
    isValid: false,
    error: "Invalid region specified",
    maskedNumber: null,
  };
}

/**
 * SECURITY: Sanitizes phone number for Firebase
 * Removes formatting, ensures E.164 format
 *
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} Sanitized phone number
 */
export function sanitizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";

  const normalized = phoneNumber.replace(/\s/g, "").replace(/[()]/g, "");

  // Ensure E.164 format (+[country][number])
  if (!normalized.startsWith("+")) {
    // Try to detect UK number
    if (normalized.startsWith("0")) {
      return `+44${normalized.slice(1)}`;
    }
    // If no prefix, assume it's incomplete
    return normalized;
  }

  return normalized;
}

/**
 * SECURITY: Generates recovery codes for MFA
 * Used as backup if phone is inaccessible
 * 🔒 Should be stored securely and shown once
 *
 * @param {number} count - Number of codes to generate (default: 8)
 * @returns {string[]} Array of recovery codes
 */
export function generateRecoveryCodes(count = 8) {
  const codes = [];

  for (let i = 0; i < count; i++) {
    // Format: XXXX-XXXX-XXXX (12 characters)
    const code = Array.from({ length: 12 })
      .map(() => Math.floor(Math.random() * 16).toString(16).toUpperCase())
      .join("");

    // Insert hyphens for readability
    const formatted = `${code.substring(0, 4)}-${code.substring(
      4,
      8
    )}-${code.substring(8, 12)}`;

    codes.push(formatted);
  }

  return codes;
}

/**
 * SECURITY: Validates recovery code format
 *
 * @param {string} code - Recovery code to validate
 * @returns {boolean} True if valid format
 */
export function isValidRecoveryCode(code) {
  if (!code || typeof code !== "string") return false;

  // Format: XXXX-XXXX-XXXX
  const pattern = /^[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}$/;
  return pattern.test(code.toUpperCase());
}

/**
 * SECURITY: Normalizes recovery code for comparison
 * Removes hyphens and converts to uppercase
 *
 * @param {string} code - Recovery code to normalize
 * @returns {string} Normalized code
 */
export function normalizeRecoveryCode(code) {
  if (!code) return "";
  return code.replace(/-/g, "").toUpperCase();
}
